import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import moment from 'moment';
import { useConfirm } from 'hooks';
import { containerSx } from './Wizard.styled';
import { formatAmount, forceTimezone } from 'utils';
import { InvoiceItemModal } from 'components/Modals';
import { InvoiceActions } from 'state/modules/invoice';
import InvoiceCard from 'components/Invoice/InvoiceCard';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import InvoiceItemsTable from 'components/Invoice/InvoiceItemsTable';

export const Details = (props) => {
  const { isStatementEntry } = props;
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const { localeFormat } = useSelector((state) => state.organizations);
  const {
    loading,
    invoice = {},
    fetchingInvoiceItem,
  } = useSelector((state) => state.invoice || {});

  const isInvoiceStatement = invoice?.receivable_type === 'statement';

  const [toggleDeleteItem, DeleteItemConfirm] = useConfirm({
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    title: i18n.t('invoice.delete_item'),
    message: i18n.t('invoice.delete_item_message'),
    onConfirm: useCallback(() => {
      dispatch(InvoiceActions.fetchDeleteItemFromInvoice(currentItem));
    }, [dispatch, currentItem]),
  });

  /**
   * Toggle confirm modal
   * @param {string} item Id of the item
   */
  const handleDeleteItem = (item) => {
    setCurrentItem(item);
    toggleDeleteItem();
  };

  /**
   * Toggle item modal
   * @param {item} item Current invoice item
   */
  const handleClickModal = (item) => {
    setCurrentItem(item);
    setOpenModal((prevValue) => !prevValue);
  };

  return (
    <Box sx={containerSx}>
      <Container className="h-100">
        <Grid container direction="row" justifyContent="center" alignItems="center">
          <Grid size={12}>
            <Grid container direction="row">
              <Grid size={{ sm: 6, md: 2 }}>
                <Typography variant="h6">
                  <Box fontWeight="500" fontSize={14}>
                    {i18n.t('common.To')}:
                  </Box>
                </Typography>
                <Typography variant="h6">
                  <Box fontSize={14} fontWeight="400">
                    {invoice?.account_customer?.customer_name}
                  </Box>
                </Typography>
              </Grid>
              <Grid size={{ sm: 6, md: 2 }}>
                <Typography variant="h6">
                  <Box fontWeight="500" fontSize={14}>
                    {i18n.t('common.category')}:
                  </Box>
                </Typography>
                <Typography variant="h6">
                  <Box fontSize={14} fontWeight="400">
                    {i18n.t(`source_types.${invoice?.source_name}`, {
                      defaultValue: invoice?.source_name,
                    })}
                  </Box>
                </Typography>
              </Grid>
              <Grid size={{ sm: 6, md: 2 }}>
                <Typography variant="h6">
                  <Box fontWeight="500" fontSize={14}>
                    {i18n.t('common.related_to')}:
                  </Box>
                </Typography>
                <Typography variant="h6">
                  <Box fontSize={14} fontWeight="400">
                    {i18n.t('common.id')}# {invoice?.source_id}
                  </Box>
                </Typography>
              </Grid>
              <Grid size={{ sm: 6, md: 2 }}>
                <Typography variant="h6">
                  <Box fontWeight="500" fontSize={14}>
                    {i18n.t('common.due_date')}:
                  </Box>
                </Typography>
                <Typography variant="h6">
                  <Box fontSize={14} fontWeight="400">
                    {invoice?.due_time
                      ? isInvoiceStatement
                        ? moment(invoice?.due_time).utc(false).format(localeFormat)
                        : forceTimezone(moment(invoice?.due_time)).format(`${localeFormat} hh:mm A`)
                      : ''}
                  </Box>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12}>
            <Box mt={3}>
              <InvoiceItemsTable
                loading={loading}
                handleEditItem={handleClickModal}
                handleCreateItem={handleClickModal}
                handleDeleteItem={handleDeleteItem}
                invoice={invoice}
              />
            </Box>
          </Grid>
          <Grid size={12}>
            <Box display="flex" mt={-5.5} justifyContent="flex-end">
              <InvoiceCard
                list={[
                  {
                    label: i18n.t('invoice.subtotal'),
                    value: '$' + formatAmount((invoice?.invoiced || 0) - (invoice?.tax || 0)),
                  },
                  {
                    label: i18n.t('invoice.total_tax'),
                    value: '$' + formatAmount(invoice?.tax),
                  },
                  {
                    label: isStatementEntry
                      ? i18n.t('common.total_amount')
                      : i18n.t('invoice.total_invoice'),
                    value: '$' + formatAmount(invoice?.invoiced),
                  },
                ]}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
      {openModal && (
        <InvoiceItemModal
          wizard={true}
          open={openModal}
          current={currentItem}
          invoice={invoice?.id}
          loading={fetchingInvoiceItem}
          handleClick={handleClickModal}
        />
      )}
      <DeleteItemConfirm />
    </Box>
  );
};

Details.propTypes = {
  isStatementEntry: PropTypes.bool,
};
